import { useDrag } from "client/hooks/utils/useDrag";
import { ResizeHandlerDirection } from "store/features/memeGenerator/types";
import { store, useAppDispatch } from "store/store";
import { useMemeContainer } from "../useMemeContainer";
import { setActiveTextBoxId, setSettings } from "store/features/memeGenerator/actions";
import { DEFAULT_FONT_SIZE } from "store/features/memeGenerator/data/configs";
import { adjustFontSize } from "store/features/memeGenerator/utils/adjustFontSize";
import { useResizeHandlers } from "store/features/memeGenerator/utils/useResizeHandlers";
import { RefObject } from "react";
import throttle from "lodash.throttle";

const TEXTBOX_MIN_HEIGHT = 50;
const TEXTBOX_MIN_WIDTH = 50;

type useResizeHandler = (
    id: string,
    direction: ResizeHandlerDirection,
    refs: {
        contentEditableRef: RefObject<HTMLElement>;
        textBoxContainerRef: RefObject<HTMLElement>;
    },
) => {
    dragHandlers: ReturnType<typeof useDrag>["dragHandlers"];
};
export const useResizeHandler: useResizeHandler = (id, direction, { contentEditableRef, textBoxContainerRef }) => {
    const dispatch = useAppDispatch();
    const { getContainer } = useMemeContainer();
    function getTextBox() {
        const textBox = store.getState().memeGenerator.meme?.textBoxes.find((_t) => _t.id === id);
        if (!textBox) throw new Error("TextBox now found");
        return textBox;
    }
    function getItem() {
        const textBox = getTextBox();
        return {
            left: textBox.left,
            top: textBox.top,
            width: textBox.width,
            height: textBox.height,
            rotation: textBox.rotation,
        };
    }
    const onEvent: Parameters<typeof useResizeHandlers>[0]["onEvent"] = (data) => {
        const textBox = getTextBox();
        if (
            (typeof data.width !== "undefined" && data.width < TEXTBOX_MIN_WIDTH) ||
            (typeof data.height !== "undefined" && data.height < TEXTBOX_MIN_HEIGHT)
        )
            return;
        dispatch(
            setSettings({
                id: textBox.id,
                ...data,
            }),
        );
        const contentEditableDiv = contentEditableRef.current;
        const textBoxContainer = textBoxContainerRef.current;
        if (!contentEditableDiv) throw new Error("Content Editable Div not found");
        if (!textBoxContainer) throw new Error("TextBox Container not found");
        adjustFontSize(contentEditableDiv, textBoxContainer, DEFAULT_FONT_SIZE);
        const fontSize = parseInt(getComputedStyle(contentEditableDiv).fontSize);
        if (!isNaN(fontSize)) {
            dispatch(
                setSettings({
                    id: textBox.id,
                    fontSize,
                }),
            );
        }
        dispatch(setActiveTextBoxId(textBox.id));
    };
    const { handleDrag } = useResizeHandlers({
        getContainer,
        getItem,
        onEvent,
    });
    function onPointerDown() {}
    function onPointerMove(clientX: number, clientY: number) {
        handleDrag({ clientX, clientY }, direction);
    }
    function onPointerUp() {}
    const { dragHandlers } = useDrag({ onPointerDown, onPointerMove, onPointerUp, positionType: "fixed" });
    return { dragHandlers };
};
